<template>
  <div class="login-container">
    <div class="login-card">
      <h1 class="login-title">Abacus Retail Dashboard</h1>
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="companyId">Company ID</label>
          <input
            type="number"
            id="companyId"
            v-model="formData.companyId"
            :class="{ 'error': errors.companyId }"
            placeholder="Enter Company ID"
            required
          />
          <span class="error-message" v-if="errors.companyId">{{ errors.companyId }}</span>
        </div>

        <div class="form-group">
          <label for="storeIds">Store IDs</label>
          <input
            type="text"
            id="storeIds"
            v-model="formData.storeIds"
            :class="{ 'error': errors.storeIds }"
            placeholder="Enter Store IDs (comma-separated)"
            required
          />
          <span class="error-message" v-if="errors.storeIds">{{ errors.storeIds }}</span>
        </div>

        <button type="submit" class="login-button" :disabled="isLoading">
          <span v-if="isLoading">Logging in...</span>
          <span v-else>Login</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import config from '@/config'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const isLoading = ref(false)
const formData = ref({
  companyId: '',
  storeIds: ''
})

const errors = ref({
  companyId: '',
  storeIds: ''
})

const validateForm = () => {
  let isValid = true
  errors.value = {
    companyId: '',
    storeIds: ''
  }

  if (!formData.value.companyId) {
    errors.value.companyId = 'Company ID is required'
    isValid = false
  } else if (isNaN(formData.value.companyId)) {
    errors.value.companyId = 'Company ID must be a number'
    isValid = false
  }

  if (!formData.value.storeIds) {
    errors.value.storeIds = 'Store IDs are required'
    isValid = false
  } else {
    const storeIds = formData.value.storeIds.split(',').map(id => id.trim())
    if (storeIds.some(id => isNaN(id))) {
      errors.value.storeIds = 'All Store IDs must be numbers'
      isValid = false
    }
  }

  return isValid
}

const handleLogin = async () => {
  if (!validateForm()) return

  isLoading.value = true
  try {
    const response = await fetch(`${config.serverUrl}/api/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        companyId: Number(formData.value.companyId),
        storeIds: formData.value.storeIds.split(',').map(id => Number(id.trim()))
      })
    })

    const data = await response.json()
    if (data.token) {
      // Store token
      localStorage.setItem('token', data.token)
      
      // Store user data
      const userData = {
        companyId: Number(formData.value.companyId),
        storeIds: formData.value.storeIds.split(',').map(id => Number(id.trim())),
        machineId: formData.value.storeIds.split(',')[0].trim() // Use first store ID as machine ID
      }
      localStorage.setItem('user', JSON.stringify(userData))
      
      router.push('/dashboard')
    }
  } catch (error) {
    console.error('Login failed:', error)
    errors.value.general = 'Login failed. Please try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f2f5;
  padding: 20px;
}

.login-card {
  background: white;
  padding: 2rem;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.login-title {
  color: #333;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: 500;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

label {
  color: #333;
  font-weight: 400;
  font-size: 0.875rem;
}

input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
}

input.error {
  border-color: #dc3545;
}

.error-message {
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.login-button {
  padding: 0.5rem 1rem;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  height: 36px;
}

.login-button:hover:not(:disabled) {
  background-color: #357abd;
}

.login-button:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}
</style> 